import React, { useEffect, useMemo, useState } from 'react'
import { Divider, SelectChangeEvent, styled, useMediaQuery, useTheme } from '@mui/material'
import useVariants, {
  Dimension,
  DimensionTypes,
  knownDimensionNames,
  Variant,
} from '../../utils/useVariations'
import DropdownList, { DropdownItem } from '../../../../components/Dropdownlist'
import { TestIds } from '../../../../testIds'
import { CartItem } from '../../../cart/storage/cart.types'
import { Product } from '../../storage'
import _ from 'lodash'
import { formatNumber } from '../../../../util/helpers'
import { ItemContainer, ItemLabel } from '../../../../components/styled'
import PrintVariants from './PrintVariants'
import { toast } from 'react-toastify'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { getAvailableColours } from '../../utils/getAvailableColours'
import { colors } from '../../../../themes/colors'

export const VariantsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  columnGap: '24px',
  rowGap: '8px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))
interface Props {
  item: Product | CartItem
  enableVariants?: boolean
  totalOrderedQty?: number
  onChangeVariant?: (variant: Variant) => void
  condensed?: boolean
}

export const ProductVariantDetails = ({
  item: { masterProductId, productId, ...item },
  enableVariants,
  onChangeVariant,
  totalOrderedQty,
  condensed,
}: Props) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const { variant, variants, dimensions, error, isLoading, defaultDimension } = useVariants(
    productId,
    masterProductId,
    !!onChangeVariant,
  )

  const allColours = dimensions
    ? dimensions[1]?.map((e) => e.value)
    : [item.colourDescription ?? item.colour ?? '']
  const [selectedSize, setSelectedSize] = useState<string | undefined>(item.sizeDescription)
  const [selectedColour, setSelectedColour] = useState<string | undefined>(item.colourDescription)

  // Always update cart item, particularly for EditPriceModal
  useEffect(() => {
    if ((item as CartItem)?.lineId) {
      setSelectedSize(item.sizeDescription)
      setSelectedColour(item.colourDescription)
    }
  }, [item])

  useEffect(() => {
    if (!selectedSize && defaultDimension) {
      if (defaultDimension.colour !== undefined) setSelectedColour(defaultDimension.colour)
      if (defaultDimension.size !== undefined) setSelectedSize(defaultDimension.size)
      if (variant && onChangeVariant) {
        onChangeVariant(variant)
      }
    }
  }, [defaultDimension, selectedSize, variant, onChangeVariant])

  const availableColours = useMemo(() => {
    if (selectedSize && selectedColour) {
      const colours = getAvailableColours(variants, selectedSize)
      const newArr: DropdownItem[] = allColours?.map((x) => {
        if (colours.find((y) => y.value === x)) return { value: x, disabled: false }
        return { value: `${x} (Unavailable)`, disabled: true }
      })
      return newArr
    }
    if (!selectedSize && selectedColour)
      return allColours.map((x) => {
        return { value: x, disabled: false }
      })
    return []
  }, [variants, selectedSize, allColours])

  const handleDimensionChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value
    const currentVariant = variants?.variants.find((v) => v.productId === productId)
    let dimension: Dimension | undefined
    variants?.variants.find((variant) => {
      dimension = variant?.dimensions.find((x) => x.value === value) ?? dimension
    })

    let colours: Dimension[] | undefined = []
    let isForceUpdateColour = false

    if (dimension?.type === DimensionTypes.SIZE) {
      setSelectedSize(dimension.value)

      if (selectedColour) {
        // check current colour in available colours
        colours = getAvailableColours(variants, dimension?.value)
        if (!colours?.find((x) => x.value === selectedColour)) {
          isForceUpdateColour = true
          toast(`${selectedColour} is unavailable`, {
            type: 'error',
            autoClose: false,
            icon: <MaterialSymbol>error</MaterialSymbol>,
          })
        }
      }
    }

    if (dimension?.type === DimensionTypes.COLOUR) {
      setSelectedColour(dimension.value)
    }

    const dmap = new Map<number, Dimension>()

    currentVariant?.dimensions.forEach((dim) => dmap.set(dim.type, dim))
    if (dimension) {
      dmap.set(dimension?.type, dimension)
    }

    // if selected colour is not available with the size, set colour as the first available options
    if (isForceUpdateColour && colours) {
      dmap.set(1, colours[0])
      setSelectedColour(colours[0].value)
    }

    const values = Array.from(dmap.values())
    const newVariant = variants?.variants.find((variant) => {
      return _.isEqual(variant?.dimensions, values)
    })

    if (newVariant && onChangeVariant) {
      onChangeVariant(newVariant)
    }
  }

  const ColourDropdown = React.useMemo(() => {
    if (enableVariants && selectedColour) {
      return (
        <DropdownList
          testId={TestIds.feat.product.variant.colourDropdown}
          sxFormControl={isSmall ? {} : { flexShrink: 1, maxWidth: '164px' }}
          sx={isSmall ? {} : { maxWidth: '164px' }}
          label={knownDimensionNames[1] ?? ''}
          items={availableColours}
          onChange={handleDimensionChange}
          value={
            variant?.dimensions.find((f) => f.type === 1)?.value ??
            item.colourDescription ??
            item.colour
          }
        />
      )
    }
  }, [
    knownDimensionNames,
    dimensions,
    variants,
    variant,
    item.colour,
    enableVariants,
    selectedColour,
    error,
    isLoading,
    isSmall,
    availableColours,
  ])

  const SizeDropdown = React.useMemo(() => {
    const allSizes = dimensions
      ? dimensions[3]?.map((e) => e.value)
      : [item.sizeDescription ?? item.size ?? '']

    if (enableVariants && selectedSize) {
      return (
        <DropdownList
          testId={TestIds.feat.product.variant.sizeDropdown}
          sxFormControl={isSmall ? {} : { flexShrink: 1, maxWidth: '164px' }}
          sx={isSmall ? {} : { maxWidth: '164px' }}
          label={knownDimensionNames[3] ?? ''}
          items={allSizes?.map((x) => {
            return { value: x, disabled: false }
          })}
          onChange={handleDimensionChange}
          value={
            variant?.dimensions.find((f) => f.type === 3)?.value ??
            item.sizeDescription ??
            item.size
          }
        />
      )
    }
  }, [
    knownDimensionNames,
    dimensions,
    defaultDimension,
    selectedSize,
    variants,
    variant,
    item.colour,
    enableVariants,
    error,
    isLoading,
    isSmall,
  ])

  const totalOrdered = React.useMemo(() => {
    if (totalOrderedQty)
      return (
        <ItemContainer wrap={isSmall}>
          <ItemLabel variant={isSmall ? 'body2' : 'caption'}>Total ordered:</ItemLabel>
          <ItemLabel variant='body2' data-testid={TestIds.feat.product.card.quantity}>
            {formatNumber(totalOrderedQty)}
          </ItemLabel>
        </ItemContainer>
      )
  }, [isSmall])

  return (
    <VariantsContainer>
      {enableVariants && (item.colour || item.size) && (
        <Divider sx={{ width: '100%', borderColor: colors.primary100 }} />
      )}
      {SizeDropdown}
      {ColourDropdown}
      {!enableVariants && (
        <>
          {(selectedSize || selectedColour || totalOrdered) && (
            <Divider sx={{ width: '100%', borderColor: colors.primary100 }} />
          )}
          {!condensed && (
            <PrintVariants
              items={[
                {
                  label: 'Size: ',
                  value: selectedSize,
                },
                {
                  label: 'Colour: ',
                  value: selectedColour,
                },
              ]}
            />
          )}

          {totalOrdered}
        </>
      )}
    </VariantsContainer>
  )
}
